import type { TypedStartListening } from '@reduxjs/toolkit';
import { AnyAction, Reducer, combineReducers, configureStore } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import * as Basket from './store/basket';
import * as ConversionId from './store/conversionId';
import * as Core from './store/core';
import * as Initialisation from './store/initialisation';
import * as ClientTracking from './store/middleware/clientTrack';
import * as Logging from './store/middleware/log';
import * as SkuDataCollection from './store/middleware/skuDataCollection';
import * as Thunk from './store/middleware/thunk';
import * as Tracking from './store/middleware/track';
import * as ModularConfigurator from './store/modularConfigurator/modularConfiguratorSlice';
import * as Options from './store/options';
import * as Room from './store/room';
import * as Selections from './store/selections';

const combinedReducer = combineReducers({
    core: Core.reducer,
    initialisation: Initialisation.reducer,
    selections: Selections.reducer,
    room: Room.reducer,
    conversionId: ConversionId.reducer,
    basket: Basket.reducer,
    options: Options.reducer,
    modularConfigurator: ModularConfigurator.reducer,
});

export type RootState = ReturnType<typeof combinedReducer>;

const rootReducer: Reducer = (state: RootState, action: AnyAction) => {
    if (action.type === 'core/setClosed') {
        // Reset session specific state on app close
        state = {
            ...state,
            core: { ...state.core, error: null },
            initialisation: Initialisation.initialState,
            selections: Selections.initialState,
            options: Options.initialState,
        } as RootState;
    }

    return combinedReducer(state, action);
};

export const store = configureStore({
    reducer: rootReducer,
    enhancers: [Sentry.createReduxEnhancer()],
    middleware: getDefaultMiddleware =>
        getDefaultMiddleware()
            .prepend(Tracking.middleware)
            .prepend(ClientTracking.middleware)
            .prepend(SkuDataCollection.middleware)
            .prepend(Logging.middleware)
            .prepend(Thunk.middleware),
});

export type AppDispatch = typeof store.dispatch;
export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
